import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { T, PageHeader, Badge } from '../components/UI';
import { useAuth } from '../context/AuthContext';

const STATUS_MAP = {
  submitted:    { label:'Submitted',    bg:'var(--blue-light)', color:'var(--blue)'   },
  under_review: { label:'Under review', bg:'var(--amber-bg)',   color:'var(--amber)'  },
  resolved:     { label:'Resolved',     bg:'var(--green-bg)',   color:'var(--green)'  },
  closed:       { label:'Closed',       bg:'var(--bg)',         color:'var(--muted)'  },
};

export default function IncidentList() {
  const navigate    = useNavigate();
  const { user }    = useAuth();
  const isOwner     = user?.role === 'owner';

  const [reports,  setReports]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('');
  const [page,     setPage]     = useState(1);
  const [total,    setTotal]    = useState(0);
  const LIMIT = 10;

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/incidents?limit=500');
      setReports(data.reports || []);
      setTotal(data.total || 0);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Stats ──
  const total_     = reports.length;
  const open       = reports.filter(r=>r.status==='submitted'||r.status==='under_review').length;
  const resolved   = reports.filter(r=>r.status==='resolved'||r.status==='closed').length;
  const injuries   = reports.filter(r=>r.hasInjuries).length;
  const thisMonth  = reports.filter(r=>{ const d=new Date(r.createdAt); const now=new Date(); return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear(); }).length;

  // ── Filtered + paged ──
  const filtered = filter ? reports.filter(r=>r.status===filter) : reports;
  const pages    = Math.ceil(filtered.length / LIMIT);
  const paged    = filtered.slice((page-1)*LIMIT, page*LIMIT);

  const th = { padding:'10px 12px', textAlign:'left', fontSize:'11px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'.5px', color:'var(--muted)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' };
  const td = { padding:'12px 12px', fontSize:'13px', color:'var(--text)', verticalAlign:'middle' };

  return (
    <div style={T.page}>
      <PageHeader title="Incident Reports" actions={
        <button onClick={()=>navigate('/incidents/new')} style={T.btnPrimary}>
          <i className="ti ti-plus" aria-hidden="true"/> Report Incident
        </button>
      }/>

      <div style={{ maxWidth:'1000px', margin:'0 auto', padding:'16px 16px 80px' }}>

        {/* ── Summary bar ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,minmax(0,1fr))', gap:'10px', marginBottom:'16px' }}>
          {[
            { label:'Total',       value:total_,   color:'var(--text)',   icon:'ti-file-description', bg:'var(--bg)' },
            { label:'Open',        value:open,     color:'var(--amber)',  icon:'ti-clock',            bg:'var(--amber-bg)' },
            { label:'Resolved',    value:resolved, color:'var(--green)',  icon:'ti-check',            bg:'var(--green-bg)' },
            { label:'With injuries',value:injuries,color:'var(--red)',    icon:'ti-alert-circle',     bg:'var(--red-bg)' },
            { label:'This month',  value:thisMonth,color:'var(--blue)',   icon:'ti-calendar',         bg:'var(--blue-light)' },
          ].map(({label,value,color,icon,bg})=>(
            <div key={label} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'14px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
                <div style={{ width:'32px', height:'32px', background:bg, borderRadius:'var(--radius)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <i className={`ti ${icon}`} style={{ color, fontSize:'16px' }} aria-hidden="true"/>
                </div>
                <span style={{ fontSize:'11px', color:'var(--muted)', fontWeight:'500' }}>{label}</span>
              </div>
              <div style={{ fontSize:'26px', fontWeight:'500', color }}>{value}</div>
            </div>
          ))}
        </div>

        {/* ── Filter tabs ── */}
        <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
          {[['','All incidents'],['submitted','Submitted'],['under_review','Under review'],['resolved','Resolved'],['closed','Closed']].map(([s,label])=>(
            <button key={s} onClick={()=>{ setFilter(s); setPage(1); }}
              style={{ padding:'6px 14px', borderRadius:'20px', border:`1px solid ${filter===s?'var(--blue)':'var(--border-md)'}`, background:filter===s?'var(--blue)':'transparent', color:filter===s?'#fff':'var(--muted)', fontSize:'12px', cursor:'pointer', fontFamily:'inherit', fontWeight:filter===s?'500':'400' }}>
              {label} {s===''?`(${total_})`:(`(${reports.filter(r=>s?r.status===s:true).length})`)}
            </button>
          ))}
        </div>

        {/* ── Table ── */}
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
          {loading ? (
            <div style={{ padding:'40px', textAlign:'center', color:'var(--muted)' }}>
              <i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/>
            </div>
          ) : paged.length === 0 ? (
            <div style={{ padding:'48px', textAlign:'center' }}>
              <i className="ti ti-alert-triangle" style={{ fontSize:'32px', color:'var(--hint)', display:'block', marginBottom:'12px' }} aria-hidden="true"/>
              <div style={{ fontSize:'14px', fontWeight:'500', color:'var(--text)', marginBottom:'6px' }}>No incidents found</div>
              <div style={{ fontSize:'13px', color:'var(--muted)', marginBottom:'16px' }}>No incidents match the selected filter.</div>
              <button onClick={()=>navigate('/incidents/new')} style={T.btnPrimary}>Report Incident</button>
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Date & time</th>
                  <th style={th}>Location</th>
                  <th style={th}>Person involved</th>
                  <th style={th}>Description</th>
                  <th style={th}>Injuries</th>
                  <th style={th}>Photos</th>
                  <th style={th}>Status</th>
                  <th style={th}>Submitted by</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r,idx)=>{
                  const st = STATUS_MAP[r.status] || STATUS_MAP.submitted;
                  return (
                    <tr key={r._id}
                      onClick={()=>navigate(`/incidents/${r._id}`)}
                      style={{ borderTop:idx>0?'1px solid var(--border)':'none', cursor:'pointer', transition:'background .1s' }}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={td}>
                        <div style={{ fontWeight:'500', color:'var(--text)', fontSize:'13px' }}>{new Date(r.date||r.createdAt).toLocaleDateString('en-AU',{day:'2-digit',month:'short',year:'numeric'})}</div>
                        <div style={{ fontSize:'11px', color:'var(--muted)', marginTop:'2px' }}>{r.time||'—'}</div>
                      </td>
                      <td style={td}>
                        <div style={{ fontSize:'12px', color:'var(--blue)' }}>
                          {r.locationType==='transit' ? `${r.transitFrom} → ${r.transitTo}` : r.location}
                        </div>
                        <div style={{ fontSize:'11px', color:'var(--hint)', marginTop:'2px' }}>{r.locationType==='transit'?'In transit':'At location'}</div>
                      </td>
                      <td style={td}>
                        <div style={{ fontSize:'13px', color:'var(--text)' }}>{r.personInvolved||'—'}</div>
                        {r.reportedBy && <div style={{ fontSize:'11px', color:'var(--muted)', marginTop:'2px' }}>By: {r.reportedBy}</div>}
                      </td>
                      <td style={{ ...td, maxWidth:'180px' }}>
                        <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:'13px', color:'var(--muted)' }}>{r.description||'—'}</div>
                      </td>
                      <td style={{ ...td, textAlign:'center' }}>
                        {r.hasInjuries
                          ? <span style={{ background:'var(--red-bg)', color:'var(--red)', fontSize:'11px', fontWeight:'500', padding:'2px 8px', borderRadius:'20px' }}>Yes</span>
                          : <span style={{ color:'var(--hint)', fontSize:'12px' }}>—</span>
                        }
                      </td>
                      <td style={{ ...td, textAlign:'center' }}>
                        <span style={{ fontSize:'12px', color:'var(--muted)' }}>
                          {r.photos?.length>0 ? <><i className="ti ti-photo" aria-hidden="true"/> {r.photos.length}</> : '—'}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{ background:st.bg, color:st.color, fontSize:'11px', fontWeight:'500', padding:'3px 10px', borderRadius:'20px', whiteSpace:'nowrap' }}>{st.label}</span>
                      </td>
                      <td style={td}>
                        <div style={{ fontSize:'12px', color:'var(--muted)' }}>{r.submittedByName||'—'}</div>
                        <div style={{ fontSize:'11px', color:'var(--hint)', marginTop:'2px' }}>{new Date(r.createdAt).toLocaleDateString('en-AU')}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderTop:'1px solid var(--border)' }}>
              <span style={{ fontSize:'13px', color:'var(--muted)' }}>{(page-1)*LIMIT+1}–{Math.min(page*LIMIT,filtered.length)} of {filtered.length}</span>
              <div style={{ display:'flex', gap:'6px' }}>
                {Array.from({length:pages},(_,i)=>(
                  <button key={i} onClick={()=>setPage(i+1)}
                    style={{ width:'32px', height:'32px', borderRadius:'var(--radius)', border:`1px solid ${page===i+1?'var(--blue)':'var(--border-md)'}`, background:page===i+1?'var(--blue)':'transparent', color:page===i+1?'#fff':'var(--muted)', fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>
                    {i+1}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
